import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { DiscountPolicy } from 'src/features/discountPolicies/utils/types';
import DeleteDiscountPolicyFormModal from 'src/features/discountPolicies/components/forms/DeleteDiscountPolicyFormModal';
import { Link } from 'react-router-dom';
import UpdateDiscountPolicyFormModal from 'src/features/discountPolicies/components/forms/UpdateDiscountPolicyFormModal';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import React from 'react';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type CellDiscountPolicyActions = {
    row: Row<DiscountPolicy>;
};

const isPastDiscountPolicy = (validTo: Date) => {
    const today = new Date();
    const compareDate = new Date(validTo);

    return compareDate < today;
};

export default function CellDiscountPolicyActions({ row }: CellDiscountPolicyActions) {
    const { formatMessage } = useIntl();
    const permissionManageDiscounts = useHasPermissions([PERMISSION.MANAGE_DISCOUNTS]);

    const [isPastDiscount, setIsPastDiscount] = React.useState(false);

    React.useEffect(() => {
        if (!row.original.valid_to) return;

        const discountPolicyValidTo = row.original.valid_to ? new Date(row.original.valid_to) : new Date();

        setIsPastDiscount(isPastDiscountPolicy(discountPolicyValidTo));
    }, [row.original.valid_to]);

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Link to={`/discount-policies/${row.original.id}`}>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EXPAND} />
                            </Button>
                        </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.dashboard} />
                    </TooltipContent>
                </Tooltip>
                {permissionManageDiscounts && !isPastDiscount && (
                    <>
                        <Tooltip>
                            <UpdateDiscountPolicyFormModal
                                semantics={{ entryName: formatMessage(discountPoliciesMessages.policy).toLowerCase() }}
                                selectedDiscountPolicy={row.original}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdateDiscountPolicyFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeleteDiscountPolicyFormModal selectedPolicy={row.original}>
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeleteDiscountPolicyFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
            </TooltipProvider>
        </div>
    );
}
